import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TeamMemberCard from '@/components/team/TeamMemberCard';
import TeamFilters from '@/components/team/TeamFilters';
import TeamStats from '@/components/team/TeamStats';

interface TeamMember {
  id: number;
  name: string;
  email: string;
  avatar_url?: string;
  role: string;
  department?: string;
  status: string;
  hourly_rate?: number;
  created_at: string;
  stats?: {
    total_hours: number;
    projects_assigned: number;
    tasks_completed: number;
    utilization: number;
  };
}

interface Filters {
  search: string;
  role: string;
  status: string;
  department: string;
}

const TeamList = () => {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    role: 'all',
    status: 'all',
    department: 'all',
  });

  const { data: members = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ['team-members', filters],
    queryFn: async () => {
      // Mock data - replace with actual API call
      return [
        {
          id: 1,
          name: 'Roman Siromskyi',
          email: 'leowebmark@gmail.com',
          avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roman',
          role: 'admin',
          department: 'Management',
          status: 'active',
          hourly_rate: 150,
          created_at: new Date().toISOString(),
          stats: {
            total_hours: 520,
            projects_assigned: 12,
            tasks_completed: 145,
            utilization: 92,
          },
        },
        {
          id: 2,
          name: 'Bohdan Buryi',
          email: 'bohdan@example.com',
          avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bohdan',
          role: 'developer',
          department: 'Engineering',
          status: 'active',
          hourly_rate: 80,
          created_at: new Date().toISOString(),
          stats: {
            total_hours: 480,
            projects_assigned: 8,
            tasks_completed: 98,
            utilization: 88,
          },
        },
        {
          id: 3,
          name: 'Vlad Dev',
          email: 'vlad@example.com',
          avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Vlad',
          role: 'developer',
          department: 'Engineering',
          status: 'active',
          hourly_rate: 75,
          created_at: new Date().toISOString(),
          stats: {
            total_hours: 460,
            projects_assigned: 6,
            tasks_completed: 82,
            utilization: 85,
          },
        },
        {
          id: 4,
          name: 'Nastya Tishchenko',
          email: 'nastya@example.com',
          avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Nastya',
          role: 'qa',
          department: 'Quality',
          status: 'active',
          hourly_rate: 60,
          created_at: new Date().toISOString(),
          stats: {
            total_hours: 380,
            projects_assigned: 10,
            tasks_completed: 156,
            utilization: 78,
          },
        },
        {
          id: 5,
          name: 'John Designer',
          email: 'john@example.com',
          avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John',
          role: 'designer',
          department: 'Design',
          status: 'on_leave',
          hourly_rate: 70,
          created_at: new Date().toISOString(),
          stats: {
            total_hours: 320,
            projects_assigned: 5,
            tasks_completed: 45,
            utilization: 65,
          },
        },
      ];
    },
  });

  const filteredMembers = members.filter((member) => {
    if (filters.search && !member.name.toLowerCase().includes(filters.search.toLowerCase()) &&
        !member.email.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.role !== 'all' && member.role !== filters.role) {
      return false;
    }
    if (filters.status !== 'all' && member.status !== filters.status) {
      return false;
    }
    if (filters.department !== 'all' && member.department !== filters.department) {
      return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Team Members</h1>
          <p className="text-muted-foreground mt-1">
            Manage team members and their roles
          </p>
        </div>
        <Link to="/team/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <TeamStats members={members} />

      {/* Filters */}
      <TeamFilters filters={filters} onFiltersChange={setFilters} />

      {/* Members Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No team members found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => (
            <TeamMemberCard key={member.id} member={member} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamList;
